import { Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Apollo } from 'apollo-angular';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { HeaderComponent } from '../../header/header.component';
import { NavComponent } from '../../navigation/nav.component';
import {
  ADMIN_FLEET_MANAGEMENT_LIST,
  ADMIN_FLEET_MANAGEMENT_STATS,
} from '@gql_queries/taxi_fleet/queries';
import {
  ACTIVATE_FLEET_OPERATOR,
  DEACTIVATE_FLEET_OPERATOR,
  UPDATE_TAXI_FLEET_ACCOUNT_STATUS,
} from '@gql_queries/taxi_fleet/mutations';
import { TaxiFleetSummaryStats } from '../taxi-fleet.mock';

type TaxiFleetStatus = 'APPROVED' | 'DECLINE' | 'FOR_APPROVAL';

interface TaxiFleetListItem {
  fleetId: string;
  fleetCode: string;
  operatorName: string;
  fleetType: string;
  status: string;
  activeVehicleCount: number;
  activeDriverCount: number;
  dateCreated: string;
  declineRemarks?: string | null;
}

@Component({
  selector: 'app-taxi-fleet-list',
  standalone: true,
  imports: [HeaderComponent, NavComponent, CommonModule, FormsModule, RouterModule],
  templateUrl: './taxi-fleet-list.component.html',
  styleUrl: './taxi-fleet-list.component.css',
})
export class TaxiFleetListComponent implements OnInit, OnDestroy {
  @ViewChild('updateStatusModal') updateStatusModal?: TemplateRef<any>;
  @ViewChild('deactivateModal') deactivateModal?: TemplateRef<any>;

  summaryStats: TaxiFleetSummaryStats = {
    totalOperators: 0,
    totalVehicles: 0,
    activeDrivers: 0,
    totalDrivers: 0,
  };
  operatorList: TaxiFleetListItem[] = [];

  searchKeyword = '';
  sortOrder: 'asc' | 'desc' = 'asc';
  currentPage = 1;
  pageSize = 15;
  totalPages = 1;
  total = 0;
  disableActionBtns = { next: false, prev: true };
  isLoading = false;
  listError = '';
  isSearchNoResults = false;

  private querySub?: Subscription;
  private searchSub?: Subscription;
  private readonly search$ = new Subject<string>();

  readonly statusTabs: { value: string; label: string }[] = [
    { value: 'all', label: 'Taxi Fleet Operators' },
    { value: 'FOR_APPROVAL', label: 'For Approval' },
    { value: 'FOR_AMENDMENT', label: 'For Amendment' },
    { value: 'DECLINE', label: 'Declined' },
  ];
  activeStatusTab = 'all';

  selectedOperator: TaxiFleetListItem | null = null;
  isUpdatingStatus = false;
  statusFormError = '';
  selectedStatus: TaxiFleetStatus = 'APPROVED';
  declineRemarks = '';
  statusOptions: { value: TaxiFleetStatus; label: string }[] = [
    { value: 'APPROVED', label: 'Approved' },
    { value: 'DECLINE', label: 'Decline' },
    { value: 'FOR_APPROVAL', label: 'For Approval' },
  ];

  constructor(
    private apollo: Apollo,
    private modalService: NgbModal,
  ) {}

  ngOnInit(): void {
    this.searchSub = this.search$
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe(() => {
        this.currentPage = 1;
        this.fetchFleetList();
      });

    this.fetchSummaryStats();
    this.fetchFleetList();
  }

  ngOnDestroy(): void {
    this.querySub?.unsubscribe();
    this.searchSub?.unsubscribe();
  }

  get hasOperators(): boolean {
    return this.operatorList.length > 0;
  }

  get hasSearchResults(): boolean {
    return this.hasOperators;
  }

  get isSearchActive(): boolean {
    return this.searchKeyword.trim() !== '' || this.activeStatusTab !== 'all';
  }

  get isDeclineSelected(): boolean {
    return this.selectedStatus === 'DECLINE';
  }

  onSearchChange(): void {
    this.search$.next(this.searchKeyword.trim());
  }

  onSortChange(): void {
    this.currentPage = 1;
    this.fetchFleetList();
  }

  nextPrev(action: 'next' | 'prev'): void {
    if (this.isLoading) {
      return;
    }

    const fromPage = this.currentPage;

    if (action === 'next') {
      this.currentPage += 1;
    } else if (action === 'prev' && this.currentPage > 1) {
      this.currentPage -= 1;
    }

    this.fetchFleetList(fromPage);
  }

  setStatusTab(tab: string): void {
    if (this.activeStatusTab === tab || this.isLoading) {
      return;
    }
    this.activeStatusTab = tab;
    this.currentPage = 1;
    this.fetchFleetList();
  }

  private fetchSummaryStats(): void {
    this.apollo
      .query({
        fetchPolicy: 'network-only',
        query: ADMIN_FLEET_MANAGEMENT_STATS,
        variables: { fleetType: 'taxi' },
      })
      .subscribe({
        next: ({ data }: any) => {
          const stats = data?.adminFleetManagementStats;
          if (!stats) {
            return;
          }
          this.summaryStats = {
            ...this.summaryStats,
            totalOperators: Number(stats.totalFleetOperators) || 0,
            activeDrivers: Number(stats.activeDrivers) || 0,
            totalDrivers: Number(stats.totalDrivers) || 0,
          };
        },
      });

    // adminFleetManagementStats.totalVehicles counts every registry status
    // (pending/active/inactive/released), which doesn't match the per-fleet
    // "active vehicles" the list below actually shows. Compute the card from
    // the same activeVehicleCount field the list uses, so the two agree.
    this.fetchActiveVehicleTotal();
  }

  private fetchActiveVehicleTotal(page: number = 1, runningTotal: number = 0): void {
    this.apollo
      .query({
        fetchPolicy: 'network-only',
        query: ADMIN_FLEET_MANAGEMENT_LIST,
        variables: { input: { fleetType: 'taxi', page, limit: 100 } },
      })
      .subscribe({
        next: ({ data }: any) => {
          const payload = data?.adminFleetManagementList;
          const items: { activeVehicleCount?: number }[] = payload?.items || [];
          const total =
            runningTotal +
            items.reduce((sum, item) => sum + (Number(item.activeVehicleCount) || 0), 0);
          const totalPages = Number(payload?.totalPages) || 1;

          if (page < totalPages) {
            this.fetchActiveVehicleTotal(page + 1, total);
            return;
          }

          this.summaryStats = { ...this.summaryStats, totalVehicles: total };
        },
      });
  }

  fetchFleetList(previousPage: number = this.currentPage): void {
    this.isLoading = true;
    this.listError = '';
    this.querySub?.unsubscribe();

    const input: Record<string, unknown> = {
      fleetType: 'taxi',
      page: this.currentPage,
      limit: this.pageSize,
      search: this.searchKeyword.trim() || null,
      sortOrder: this.sortOrder,
    };

    if (this.activeStatusTab !== 'all') {
      input['status'] = this.activeStatusTab;
    }

    this.querySub = this.apollo
      .query({
        fetchPolicy: 'network-only',
        query: ADMIN_FLEET_MANAGEMENT_LIST,
        variables: { input },
      })
      .subscribe({
        next: ({ data }: any) => {
          const page = data?.adminFleetManagementList;
          const items: TaxiFleetListItem[] = Array.isArray(page?.items) ? page.items : [];

          if (items.length === 0 && this.currentPage > 1) {
            this.currentPage = previousPage;
            this.disableActionBtns.next = true;
            this.isLoading = false;
            return;
          }

          this.operatorList = items.map((item) => ({
            ...item,
            activeVehicleCount: Number(item.activeVehicleCount) || 0,
            activeDriverCount: Number(item.activeDriverCount) || 0,
          }));
          this.total = Number(page?.total) || 0;
          this.totalPages = Math.max(1, Number(page?.totalPages) || 1);
          this.currentPage = Number(page?.page) || this.currentPage;
          this.isSearchNoResults = this.isSearchActive && this.operatorList.length === 0;
          this.updatePagination();
          this.isLoading = false;
        },
        error: (error) => {
          this.listError = error?.message || 'Unable to load Taxi Fleet Operators.';
          this.operatorList = [];
          this.total = 0;
          this.totalPages = 1;
          this.isSearchNoResults = false;
          this.updatePagination();
          this.isLoading = false;
        },
      });
  }

  updatePagination(): void {
    this.disableActionBtns.prev = this.currentPage <= 1;
    this.disableActionBtns.next = this.currentPage >= this.totalPages;
  }

  getVehicleCount(operator: TaxiFleetListItem): number {
    return operator.activeVehicleCount;
  }

  getStatusLabel(status: string): string {
    const normalizedStatus = (status || '').toLowerCase();
    if (normalizedStatus === 'for_approval' || normalizedStatus === 'enrollment') {
      return 'For Approval';
    }

    return status
      .replace(/_/g, ' ')
      .toLowerCase()
      .replace(/\b\w/g, (char) => char.toUpperCase());
  }

  getStatusClass(status: string): string {
    const normalizedStatus = status.toLowerCase();

    if (normalizedStatus === 'active' || normalizedStatus === 'approved') {
      return 'stat-active';
    }

    if (normalizedStatus === 'for_approval') {
      return 'stat-approval';
    }

    if (normalizedStatus === 'for_amendment') {
      return 'stat-amend';
    }

    if (normalizedStatus === 'decline' || normalizedStatus === 'declined' || normalizedStatus === 'rejected') {
      return 'stat-declined';
    }

    if (normalizedStatus === 'inactive' || normalizedStatus === 'deactivated') {
      return 'stat-inactive';
    }

    return 'stat-pending';
  }

  isActiveStatus(status: string): boolean {
    const normalizedStatus = (status || '').toLowerCase();
    return normalizedStatus === 'active' || normalizedStatus === 'approved';
  }

  /** OP2-119: Activate for Inactive (Expired not yet in API enum). */
  canActivateOperator(status: string): boolean {
    const normalizedStatus = (status || '').toLowerCase();
    return normalizedStatus === 'inactive' || normalizedStatus === 'deactivated';
  }

  /** OP2-119: Deactivate for Active/Approved. */
  canDeactivateOperator(status: string): boolean {
    return this.isActiveStatus(status);
  }

  openDeactivateModal(operator: TaxiFleetListItem, modal: TemplateRef<any>): void {
    this.selectedOperator = operator;
    this.declineRemarks = '';
    this.statusFormError = '';
    this.modalService.open(modal, {
      size: 'md',
      backdrop: 'static',
      keyboard: false,
    });
  }

  confirmDeactivate(modal: NgbModalRef): void {
    // Notes are UI-only today; deactivateFleetOperatorInput only accepts fleetId.
    this.deactivateFleetOperator(() => modal.close());
  }

  private deactivateFleetOperator(onSuccess?: () => void): void {
    if (!this.selectedOperator?.fleetId || this.isUpdatingStatus) {
      return;
    }

    this.isUpdatingStatus = true;
    this.statusFormError = '';

    this.apollo
      .mutate({
        mutation: DEACTIVATE_FLEET_OPERATOR,
        variables: {
          input: {
            fleetId: this.selectedOperator.fleetId,
          },
        },
      })
      .subscribe({
        next: (result: any) => {
          const updatedFleet = result?.data?.deactivateFleetOperator;

          if (updatedFleet) {
            this.patchOperatorInLists(updatedFleet);
            this.isUpdatingStatus = false;
            this.selectedOperator = null;
            onSuccess?.();
            return;
          }

          this.isUpdatingStatus = false;
          this.statusFormError =
            result?.errors?.[0]?.message ||
            'Unable to deactivate fleet operator. Please try again.';
        },
        error: (error) => {
          this.isUpdatingStatus = false;
          this.statusFormError = this.mapStatusActionError(
            error,
            'Unable to deactivate fleet operator. Please try again.',
          );
        },
      });
  }

  openActivateModal(operator: TaxiFleetListItem, modal: TemplateRef<any>): void {
    this.selectedOperator = operator;
    this.statusFormError = '';
    this.modalService.open(modal, {
      size: 'md',
      backdrop: 'static',
      keyboard: false,
    });
  }

  confirmActivate(modal: NgbModalRef): void {
    this.activateFleetOperator(() => modal.close());
  }

  private activateFleetOperator(onSuccess?: () => void): void {
    if (!this.selectedOperator?.fleetId || this.isUpdatingStatus) {
      return;
    }

    this.isUpdatingStatus = true;
    this.statusFormError = '';

    this.apollo
      .mutate({
        mutation: ACTIVATE_FLEET_OPERATOR,
        variables: {
          input: {
            fleetId: this.selectedOperator.fleetId,
          },
        },
      })
      .subscribe({
        next: (result: any) => {
          const updatedFleet = result?.data?.activateFleetOperator;

          if (updatedFleet) {
            this.patchOperatorInLists(updatedFleet);
            this.isUpdatingStatus = false;
            this.selectedOperator = null;
            onSuccess?.();
            return;
          }

          this.isUpdatingStatus = false;
          this.statusFormError =
            result?.errors?.[0]?.message ||
            'Unable to activate fleet operator. Please try again.';
        },
        error: (error) => {
          this.isUpdatingStatus = false;
          this.statusFormError = this.mapStatusActionError(
            error,
            'Unable to activate fleet operator. Please try again.',
          );
        },
      });
  }

  private mapStatusActionError(error: any, fallback: string): string {
    return (
      error?.message ||
      error?.graphQLErrors?.[0]?.message ||
      error?.networkError?.error?.errors?.[0]?.message ||
      fallback
    );
  }

  onStatusChange(): void {
    this.statusFormError = '';
    if (!this.isDeclineSelected) {
      this.declineRemarks = '';
    }
  }

  confirmUpdateStatus(modal: NgbModalRef): void {
    if (!this.selectedStatus) {
      this.statusFormError = 'Please select a status.';
      return;
    }

    if (this.isDeclineSelected && !this.declineRemarks.trim()) {
      this.statusFormError = 'Please provide decline remarks.';
      return;
    }

    this.updateFleetStatus(
      this.selectedStatus,
      this.isDeclineSelected ? this.declineRemarks.trim() : undefined,
      () => modal.close(),
    );
  }

  private updateFleetStatus(
    status: TaxiFleetStatus,
    declineRemarks?: string,
    onSuccess?: () => void,
  ): void {
    if (!this.selectedOperator?.fleetId || this.isUpdatingStatus) {
      return;
    }

    this.isUpdatingStatus = true;
    this.statusFormError = '';

    const input: {
      fleetId: string;
      status: TaxiFleetStatus;
      declineRemarks?: string;
    } = {
      fleetId: this.selectedOperator.fleetId,
      status,
    };

    if (declineRemarks) {
      input.declineRemarks = declineRemarks;
    }

    this.apollo
      .mutate({
        mutation: UPDATE_TAXI_FLEET_ACCOUNT_STATUS,
        variables: { input },
      })
      .subscribe({
        next: (result: any) => {
          const updatedFleet = result?.data?.updateFleetStatus;

          if (updatedFleet) {
            this.patchOperatorInLists(updatedFleet);
            this.isUpdatingStatus = false;
            this.selectedOperator = null;
            onSuccess?.();
            return;
          }

          this.isUpdatingStatus = false;
          this.statusFormError = 'Unable to update fleet status. Please try again.';
        },
        error: (error) => {
          this.isUpdatingStatus = false;
          this.statusFormError = error?.message || 'Unable to update fleet status. Please try again.';
        },
      });
  }

  private patchOperatorInLists(updatedFleet: Partial<TaxiFleetListItem> & { fleetId: string }): void {
    this.operatorList = this.operatorList.map((operator) =>
      operator.fleetId === updatedFleet.fleetId
        ? {
            ...operator,
            status: updatedFleet.status ?? operator.status,
            declineRemarks: updatedFleet.declineRemarks ?? operator.declineRemarks,
          }
        : operator,
    );
  }
}
